import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ExternalLink, Package, TrendingUp, TrendingDown, Minus, MapPin, Loader2 } from "lucide-react";
import { fmtKr, safeNum, orDash, MiniBarChart } from "@/components/dashboard/Primitives";
import { fetchMaterialUsage30d, formatApiError } from "@/lib/api";

function TrendBadge({ pct }) {
    if (pct === null || pct === undefined) {
        return <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><Minus className="w-3 h-3" /> Intet sammenligningsgrundlag</span>;
    }
    if (pct === 0) return <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><Minus className="w-3 h-3" /> Uændret vs. forrige 30 dage</span>;
    if (pct > 0) return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700"><TrendingUp className="w-3 h-3" /> +{pct}% vs. forrige 30 dage</span>;
    return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700"><TrendingDown className="w-3 h-3" /> {pct}% vs. forrige 30 dage</span>;
}

export default function MaterialDetailModal({ material, jobs = [], jobDetails = {}, customers = [], open, onClose }) {
    const [usage, setUsage] = useState(null);
    const [loadingUsage, setLoadingUsage] = useState(false);
    const [usageError, setUsageError] = useState(null);

    useEffect(() => {
        if (!open || !material?.id) return;
        let cancelled = false;
        setLoadingUsage(true);
        setUsageError(null);
        fetchMaterialUsage30d(material.id)
            .then((data) => { if (!cancelled) setUsage(data); })
            .catch((err) => { if (!cancelled) setUsageError(formatApiError(err)); })
            .finally(() => { if (!cancelled) setLoadingUsage(false); });
        return () => { cancelled = true; };
    }, [open, material?.id]);

    const usageJobs = useMemo(() => {
        if (!material) return [];
        const out = [];
        Object.values(jobDetails).forEach((d) => {
            const found = (d.materials || []).find((m) => (m.name || "").toLowerCase() === (material.name || "").toLowerCase());
            if (found) {
                const job = jobs.find((j) => j.id === d.id) || d;
                out.push({ job_id: job.id, customer: job.customer || "Ukendt", qty: found.qty, price: found.price });
            }
        });
        return out;
    }, [material, jobs, jobDetails]);

    if (!material) return null;

    const usedCustomers = Array.from(new Set(usageJobs.map((x) => x.customer)));
    const isLow = safeNum(material.stock) <= safeNum(material.min_qty);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3 flex-wrap">
                        <Package className="w-5 h-5 text-blue-600" />
                        <span>{material.name || "Materiale"}</span>
                        <span className="font-mono text-xs text-slate-400">{material.id}</span>
                    </DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                    <div className={`rounded-xl p-3 ${isLow ? "bg-red-50 border border-red-100" : "bg-slate-50"}`}>
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Lager</p>
                        <p className={`text-2xl font-bold ${isLow ? "text-red-600" : "text-slate-900"}`}>{safeNum(material.stock)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Minimum</p>
                        <p className="text-2xl font-bold text-slate-900">{safeNum(material.min_qty)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Købspris</p>
                        <p className="text-xl font-bold text-slate-900">{fmtKr(material.buy_price)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Salgspris</p>
                        <p className="text-xl font-bold text-emerald-600">{fmtKr(material.sell_price)}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <div className="bg-white border border-slate-100 rounded-xl p-3 text-sm">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" />Hyldeplacering</p>
                        <p className="text-slate-900 font-semibold">{orDash(material.shelf)}</p>
                    </div>
                    <div className="bg-white border border-slate-100 rounded-xl p-3 text-sm">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Leverandør</p>
                        {material.link ? (
                            <a href={material.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-semibold">
                                {material.supplier || "Link"} <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        ) : <p className="text-slate-900 font-semibold">{orDash(material.supplier)}</p>}
                    </div>
                </div>

                {isLow && (
                    <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-sm text-amber-900">
                        <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                        <span>Lageret er under minimum. Bestil mere hos <strong>{material.supplier || "leverandør"}</strong>.</span>
                    </div>
                )}

                <section className="mt-5" data-testid="material-usage-30d">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-slate-400" />Forbrug — sidste 30 dage</h3>
                        {usage && <TrendBadge pct={usage.trend_pct} />}
                    </div>

                    {loadingUsage && (
                        <div className="bg-white border border-slate-100 rounded-xl p-6 flex items-center justify-center text-sm text-slate-500 gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Henter forbrug…
                        </div>
                    )}

                    {!loadingUsage && usageError && (
                        <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-sm text-red-700">
                            Kunne ikke hente forbrug: {usageError}
                        </div>
                    )}

                    {!loadingUsage && !usageError && usage && (
                        <>
                            {usage.has_data ? (
                                <div className="bg-white border border-slate-100 rounded-xl p-3">
                                    <MiniBarChart data={usage.daily_series} color="#3b82f6" />
                                </div>
                            ) : (
                                <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-6 text-center text-sm text-slate-500">
                                    Ingen registreret forbrug de sidste 30 dage endnu.
                                    <p className="text-[11px] text-slate-400 mt-1">Forbrug registreres automatisk når medarbejdere bruger materialet på et job.</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-3 mt-3 text-center">
                                <div className="bg-slate-50 rounded-xl p-3" data-testid="usage-30d-total">
                                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Sidste 30 dage</p>
                                    <p className="text-lg font-bold text-slate-900">{safeNum(usage.total_qty_30d)}</p>
                                </div>
                                <div className="bg-slate-50 rounded-xl p-3" data-testid="usage-30d-prev">
                                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Forrige 30 dage</p>
                                    <p className="text-lg font-bold text-slate-500">{safeNum(usage.total_qty_prev_30d)}</p>
                                </div>
                            </div>
                        </>
                    )}
                </section>

                <section className="mt-5">
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Brugt på jobs ({usageJobs.length})</h3>
                    {usageJobs.length === 0 ? (
                        <p className="text-sm text-slate-400 italic">Ingen jobs har brugt dette materiale endnu.</p>
                    ) : (
                        <ul className="divide-y divide-slate-100 bg-white rounded-xl border border-slate-100 max-h-60 overflow-y-auto">
                            {usageJobs.map((u, i) => (
                                <li key={i} className="px-3 py-2 flex items-center justify-between text-sm">
                                    <div>
                                        <span className="font-mono text-slate-500">{u.job_id}</span>
                                        <span className="text-slate-700 ml-2">{u.customer}</span>
                                    </div>
                                    <span className="text-slate-600">{safeNum(u.qty)} stk · {fmtKr(u.price)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {usedCustomers.length > 0 && (
                    <section className="mt-5">
                        <h3 className="text-sm font-semibold text-slate-700 mb-2">Brugt hos kunder ({usedCustomers.length})</h3>
                        <div className="flex flex-wrap gap-1.5">
                            {usedCustomers.map((c) => (
                                <span key={c} className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">{c}</span>
                            ))}
                        </div>
                    </section>
                )}

                <div className="mt-6 flex justify-end">
                    <Button data-testid="material-detail-close" onClick={onClose} className="bg-slate-900 hover:bg-slate-800 text-white">Luk</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
